/**
 * Handler Centralizado de Erros
 * Responsável por capturar, processar e reportar erros do SDK
 */

export class ErrorHandler {
    constructor(logger) {
        // Validação e inicialização segura do logger
        if (!logger || typeof logger !== 'object') {
            console.warn('ErrorHandler: Logger inválido fornecido, usando fallback');
            this.logger = this.createFallbackLogger();
        } else {
            // Verificar se o logger tem os métodos necessários
            const requiredMethods = ['info', 'warn', 'error', 'debug'];
            const missingMethods = requiredMethods.filter(method => typeof logger[method] !== 'function');
            
            if (missingMethods.length > 0) {
                console.warn(`ErrorHandler: Logger está faltando métodos: ${missingMethods.join(', ')}, usando fallback`);
                this.logger = this.createFallbackLogger();
            } else {
                this.logger = logger;
            }
        }
        
        this.errorCounts = new Map();
        this.maxErrorsPerType = 10;
        this.errorCallbacks = new Set();
        
        // Captura erros não tratados
        this.setupGlobalErrorHandling();
    }
    
    /**
     * Cria um logger de fallback quando o logger principal falha
     */
    createFallbackLogger() {
        return {
            info: (msg, data) => console.log(`[INFO] ${msg}`, data || ''),
            warn: (msg, data) => console.warn(`[WARN] ${msg}`, data || ''),
            error: (msg, data) => console.error(`[ERROR] ${msg}`, data || ''),
            debug: (msg, data) => console.log(`[DEBUG] ${msg}`, data || '')
        };
    }
    
    /**
     * Tipos de erro conhecidos
     */
    static get ERROR_TYPES() {
        return {
            NETWORK_ERROR: 'network_error',
            PERMISSION_DENIED: 'permission_denied',
            FEATURE_NOT_SUPPORTED: 'feature_not_supported',
            TIMEOUT_ERROR: 'timeout_error',
            VALIDATION_ERROR: 'validation_error',
            CANVAS_ERROR: 'canvas_error',
            WEBGL_ERROR: 'webgl_error',
            AUDIO_ERROR: 'audio_error',
            BEHAVIORAL_ERROR: 'behavioral_error',
            PRIVACY_ERROR: 'privacy_error',
            DEVICE_ERROR: 'device_error',
            UNKNOWN_ERROR: 'unknown_error'
        };
    }
    
    /**
     * Severidade dos erros
     */
    static get SEVERITY() {
        return {
            LOW: 'low',
            MEDIUM: 'medium',
            HIGH: 'high',
            CRITICAL: 'critical'
        };
    }
    
    /**
     * Configura captura global de erros
     */
    setupGlobalErrorHandling() {
        if (typeof window !== 'undefined') {
            // Captura erros JavaScript não tratados
            window.addEventListener('error', (event) => {
                this.handleGlobalError(event.error, 'unhandled_error', {
                    filename: event.filename,
                    lineno: event.lineno,
                    colno: event.colno
                });
            });
            
            // Captura promises rejeitadas não tratadas
            window.addEventListener('unhandledrejection', (event) => {
                this.handleGlobalError(event.reason, 'unhandled_promise_rejection', {
                    promise: event.promise
                });
            });
        }
    }
    
    /**
     * Manipula erro principal
     */
    handle(error, context = 'unknown', additionalData = {}) {
        const errorInfo = this.processError(error, context, additionalData);
        
        // Verifica se deve ignorar por limite de frequência
        if (this.shouldIgnoreError(errorInfo.type)) {
            return errorInfo;
        }
        
        // Registra erro
        this.logError(errorInfo);
        
        // Notifica callbacks registrados
        this.notifyCallbacks(errorInfo);
        
        // Reporta para sistemas externos se configurado
        this.reportToExternalSystems(errorInfo);
        
        return errorInfo;
    }
    
    /**
     * Processa e classifica erro
     */
    processError(error, context, additionalData) {
        const timestamp = new Date().toISOString();
        const errorType = this.classifyError(error);
        const severity = this.determineSeverity(error, errorType);
        
        return {
            id: this.generateErrorId(),
            timestamp,
            type: errorType,
            severity,
            context,
            message: error?.message || String(error),
            stack: error?.stack,
            name: error?.name,
            code: error?.code,
            additionalData,
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
            url: typeof window !== 'undefined' ? window.location.href : null
        };
    }
    
    /**
     * Classifica tipo do erro
     */
    classifyError(error) {
        if (!error) return ErrorHandler.ERROR_TYPES.UNKNOWN_ERROR;
        
        const message = error.message?.toLowerCase() || '';
        const name = error.name?.toLowerCase() || '';
        
        // Erros de rede
        if (message.includes('fetch') || message.includes('network') || 
            message.includes('connection') || name === 'networkerror') {
            return ErrorHandler.ERROR_TYPES.NETWORK_ERROR;
        }
        
        // Erros de permissão
        if (message.includes('permission') || message.includes('denied') ||
            name === 'notallowederror') {
            return ErrorHandler.ERROR_TYPES.PERMISSION_DENIED;
        }
        
        // Recursos não suportados
        if (message.includes('not supported') || message.includes('undefined') ||
            name === 'notsupportederror') {
            return ErrorHandler.ERROR_TYPES.FEATURE_NOT_SUPPORTED;
        }
        
        // Timeout
        if (message.includes('timeout') || name === 'timeouterror') {
            return ErrorHandler.ERROR_TYPES.TIMEOUT_ERROR;
        }
        
        // Validação
        if (message.includes('validation') || message.includes('invalid')) {
            return ErrorHandler.ERROR_TYPES.VALIDATION_ERROR;
        }
        
        // Canvas
        if (message.includes('canvas') || message.includes('2d')) {
            return ErrorHandler.ERROR_TYPES.CANVAS_ERROR;
        }
        
        // WebGL
        if (message.includes('webgl') || message.includes('gl')) {
            return ErrorHandler.ERROR_TYPES.WEBGL_ERROR;
        }
        
        // Audio
        if (message.includes('audio') || message.includes('webaudio')) {
            return ErrorHandler.ERROR_TYPES.AUDIO_ERROR;
        }
        
        return ErrorHandler.ERROR_TYPES.UNKNOWN_ERROR;
    }
    
    /**
     * Determina severidade do erro
     */
    determineSeverity(error, errorType) {
        // Erros críticos que impedem funcionamento
        if (errorType === ErrorHandler.ERROR_TYPES.VALIDATION_ERROR) {
            return ErrorHandler.SEVERITY.CRITICAL;
        }
        
        // Erros de alta severidade
        if ([ErrorHandler.ERROR_TYPES.NETWORK_ERROR, 
             ErrorHandler.ERROR_TYPES.TIMEOUT_ERROR].includes(errorType)) {
            return ErrorHandler.SEVERITY.HIGH;
        }
        
        // Erros de média severidade
        if ([ErrorHandler.ERROR_TYPES.PERMISSION_DENIED,
             ErrorHandler.ERROR_TYPES.FEATURE_NOT_SUPPORTED].includes(errorType)) {
            return ErrorHandler.SEVERITY.MEDIUM;
        }
        
        // Erros de baixa severidade (não impedem funcionamento)
        return ErrorHandler.SEVERITY.LOW;
    }
    
    /**
     * Verifica se deve ignorar erro por frequência
     */
    shouldIgnoreError(errorType) {
        const count = this.errorCounts.get(errorType) || 0;
        
        if (count >= this.maxErrorsPerType) {
            return true;
        }
        
        this.errorCounts.set(errorType, count + 1);
        return false;
    }
    
    /**
     * Registra erro no sistema de logging
     */
    logError(errorInfo) {
        const logMethod = this.getLogMethod(errorInfo.severity);
        
        // Verificação de segurança para evitar erro 'this.logger[t] is not a function'
        if (!this.logger || typeof this.logger[logMethod] !== 'function') {
            console.error(`Logger method '${logMethod}' not found or not a function`, {
                loggerExists: !!this.logger,
                loggerType: typeof this.logger,
                methodType: typeof this.logger?.[logMethod],
                availableMethods: this.logger ? Object.getOwnPropertyNames(Object.getPrototypeOf(this.logger)) : 'N/A'
            });
            // Fallback para console.error
            console.error(`Erro capturado: ${errorInfo.message}`, {
                errorId: errorInfo.id,
                type: errorInfo.type,
                severity: errorInfo.severity,
                context: errorInfo.context,
                stack: errorInfo.stack,
                additionalData: errorInfo.additionalData
            });
            return;
        }
        
        this.logger[logMethod](`Erro capturado: ${errorInfo.message}`, {
            errorId: errorInfo.id,
            type: errorInfo.type,
            severity: errorInfo.severity,
            context: errorInfo.context,
            stack: errorInfo.stack,
            additionalData: errorInfo.additionalData
        });
    }
    
    /**
     * Obtém método de log baseado na severidade
     */
    getLogMethod(severity) {
        switch (severity) {
            case ErrorHandler.SEVERITY.CRITICAL:
            case ErrorHandler.SEVERITY.HIGH:
                return 'error';
            case ErrorHandler.SEVERITY.MEDIUM:
                return 'warn';
            default:
                return 'debug';
        }
    }
    
    /**
     * Gera ID único para o erro
     */
    generateErrorId() {
        return 'err_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    }
    
    /**
     * Manipula erros globais
     */
    handleGlobalError(error, context, additionalData) {
        this.handle(error, context, {
            ...additionalData,
            global: true
        });
    }
    
    /**
     * Registra callback para notificação de erros
     */
    onError(callback) {
        if (typeof callback === 'function') {
            this.errorCallbacks.add(callback);
        }
    }
    
    /**
     * Remove callback de erro
     */
    offError(callback) {
        this.errorCallbacks.delete(callback);
    }
    
    /**
     * Notifica callbacks registrados
     */
    notifyCallbacks(errorInfo) {
        this.errorCallbacks.forEach(callback => {
            try {
                callback(errorInfo);
            } catch (callbackError) {
                this.logger.warn('Erro em callback de erro', { 
                    callbackError: callbackError.message 
                });
            }
        });
    }
    
    /**
     * Reporta para sistemas externos
     */
    reportToExternalSystems(errorInfo) {
        // Integração com Sentry, LogRocket, etc.
        if (typeof window !== 'undefined' && window.fingerprintSDKErrorReporter) {
            try {
                window.fingerprintSDKErrorReporter(errorInfo);
            } catch (reportError) {
                this.logger.debug('Falha ao reportar erro externamente', {
                    reportError: reportError.message
                });
            }
        }
    }
    
    /**
     * Wrapper para execução segura de funções
     */
    async safeExecute(fn, context = 'unknown', defaultValue = null) {
        try {
            return await fn();
        } catch (error) {
            this.handle(error, context);
            return defaultValue;
        }
    }
    
    /**
     * Wrapper para execução segura síncrona
     */
    safeExecuteSync(fn, context = 'unknown', defaultValue = null) {
        try {
            return fn();
        } catch (error) {
            this.handle(error, context);
            return defaultValue;
        }
    }
    
    /**
     * Obtém estatísticas de erros
     */
    getErrorStats() {
        const stats = {
            totalErrors: 0,
            byType: {},
            bySeverity: {}
        };
        
        for (const [type, count] of this.errorCounts.entries()) {
            stats.totalErrors += count;
            stats.byType[type] = count;
        }
        
        return stats;
    }
    
    /**
     * Limpa contadores de erro
     */
    clearErrorCounts() {
        this.errorCounts.clear();
        this.logger.debug('Contadores de erro limpos');
    }
    
    /**
     * Configura reporter externo
     */
    static setExternalReporter(reporter) {
        if (typeof window !== 'undefined') {
            window.fingerprintSDKErrorReporter = reporter;
        }
    }
    
    /**
     * Remove reporter externo
     */
    static removeExternalReporter() {
        if (typeof window !== 'undefined') {
            delete window.fingerprintSDKErrorReporter;
        }
    }
    
    /**
     * Cria erro customizado do SDK
     */
    static createError(message, type = ErrorHandler.ERROR_TYPES.UNKNOWN_ERROR, code = null) {
        const error = new Error(message);
        error.name = 'FingerprintSDKError';
        error.type = type;
        if (code) error.code = code;
        return error;
    }
}